/**
 * CompletionBadges — small reusable pieces used across the LMS to celebrate
 * course progress and completion.
 *
 *   <CompletedPill />          Emerald pill with check icon, safe on any bg.
 *   <ModuleCompletePill />     Compact pill for module rows (all lessons done).
 *   <CompletionRibbon />       Gold diagonal ribbon overlay for hero cards.
 *   <ProgressBar />            Same visual we use on catalog cards, reusable.
 */
import React from 'react';
import { CheckCircle2, Award } from 'lucide-react';

export const CompletedPill = ({ className = '', size = 'sm', testid = 'completed-pill' }) => {
  const dims = size === 'lg'
    ? 'text-xs px-3 py-1'
    : 'text-[10px] px-2 py-0.5';
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full font-extrabold uppercase tracking-widest bg-emerald-500 text-white shadow-sm ${dims} ${className}`}
      data-testid={testid}
    >
      <CheckCircle2 className={size === 'lg' ? 'w-3.5 h-3.5' : 'w-3 h-3'} />
      Completed
    </span>
  );
};

export const ModuleCompletePill = ({ className = '', testid = 'module-complete-pill' }) => (
  <span
    className={`inline-flex items-center gap-1 rounded-full text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 bg-emerald-500/15 text-emerald-600 dark:text-emerald-300 border border-emerald-500/30 ${className}`}
    data-testid={testid}
  >
    <CheckCircle2 className="w-3 h-3" />
    Complete
  </span>
);

/** Gold diagonal ribbon for the top-right corner of hero cards. Position the
 *  parent as `relative overflow-hidden` so the ribbon clips cleanly. */
export const CompletionRibbon = ({ label = 'Completed', testid = 'completion-ribbon' }) => (
  <div
    className="pointer-events-none absolute top-4 -right-12 rotate-45 select-none"
    aria-hidden="true"
    data-testid={testid}
  >
    <div className="inline-flex items-center gap-1.5 bg-gradient-to-r from-amber-400 to-amber-600 text-navy-900 px-14 py-1 text-[11px] font-extrabold uppercase tracking-widest shadow-lg">
      <Award className="w-3.5 h-3.5" />
      {label}
    </div>
  </div>
);

/** Slim progress bar used across LMS surfaces. */
export const ProgressBar = ({ pct = 0, testid = 'lms-progress-bar' }) => {
  const p = Math.max(0, Math.min(100, Number(pct) || 0));
  const done = p >= 100;
  return (
    <div className="h-1.5 rounded-full bg-muted overflow-hidden" data-testid={testid}>
      <div
        className={`h-full transition-all ${done ? 'bg-emerald-500' : 'bg-primary'}`}
        style={{ width: `${Math.max(2, p)}%` }}
      />
    </div>
  );
};

export default CompletedPill;
